import React, { useEffect, useRef } from 'react';
import PageWrapper from 'Pages/PageWrapper';
import SignIn from 'Pages/SignIn';
import { matchPath, Route, Switch } from 'react-router-dom';
import { compile } from 'path-to-regexp';
import { PrivateRoute } from './PrivateRoute';
import { Location } from 'history';

/**
 * TODO
 * - Transition blocking when leaving from dirty form
 * - Authorization: admin, manager, trader, viewer
 * - Flag based routing, dynamic routing, feature toggle
 */

export const UUID =
  '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

export const ORDERPID = '[BO]-[A-Z0-9]{8}-\\d+';

export const PRIVATE_ROUTES = {
  home: '/',
  homePage: '/home',
  overview: '/overview',
  marketplace: '/marketplace',
  marketdepth: `/marketplace/:productId(${UUID})`,

  dashboard: '/dashboard',

  products: '/products',
  product: `/products/:id(${UUID})/quick`,
  productSpecifications: `/products/:id(${UUID})/specifications`,

  tenders: '/tenders',

  orders: '/orders',
  orderNew: '/orders/:orderType(bid|offer)',
  orderEdit: `/orders/:orderId(${ORDERPID})/edit`,
  liquidityProvider: '/lp-overview',
  trades: '/trades',

  account: '/account',

  config: '/config',

  routes: '/routes',

  participants: '/participants',
  locations: '/locations',
  customers: '/customers',
  consignees: '/partners',
  customerRequest: '/customer-requests',
  reports: '/reports',

  consolidation: '/consolidation',
  deliveries: '/deliveries',
  users: '/users',

  futures: '/futures',
  duplicate: '/duplicate', // duplicate (tender) orders
};

export const ROUTES = {
  signIn: '/signin',
  loginEmail: '/login/email',
  ...PRIVATE_ROUTES,
};

export function routeUrl(route: string, params: Record<string, string> = {}) {
  return compile(route)(params);
}

export function useBaseUrl(
  base: string,
  baseParams: Record<string, string> = {}
) {
  return [
    // route
    function (route: string, params?: Record<string, string>) {
      return routeUrl([base, route].join('/'), { ...baseParams, ...params });
    },
    // match
    function (route: string) {
      return `${base}/${route}`;
    },
  ];
}

export function checkIsModal(pathname: string) {
  return [
    '/preview',
    ROUTES.orderNew,
    ROUTES.orderEdit,
    ROUTES.marketplace,
    ROUTES.marketdepth,
    ROUTES.product,
    ROUTES.productSpecifications,
    ROUTES.account,
    ROUTES.futures,
    ROUTES.duplicate,
  ].some((path) => matchPath(pathname, { path }));
}

export function checkIsTradeModal(pathname: string) {
  return matchPath(pathname, '/preview/trade/:id');
}

export function usePreviousNormalLocation(value: Location) {
  const ref = useRef<Location>();
  useEffect(() => {
    if (!checkIsModal(value.pathname)) {
      ref.current = value;
    }
  });
  return ref.current;
}

interface RoutesProps {
  user: unknown;
}

export function Routes({ user }: RoutesProps) {
  return (
    <Switch>
      <Route path={[ROUTES.signIn, ROUTES.loginEmail]} component={SignIn} />
      <PrivateRoute
        path={Object.values(PRIVATE_ROUTES)}
        component={PageWrapper}
        user={user}
      />
    </Switch>
  );
}
